import { ref, computed } from 'vue';
import { fetchDatasets, createDataset, updateDataset } from '@/services/api';
import { useToast } from '@/composables/use-toast';

export function useDatasets() {
  const { addToast } = useToast();
  const datasets = ref([]);
  const isLoading = ref(false);
  const currentPage = ref(1);
  const pageSize = ref(12);
  const hasMoreItems = ref(true);

  const processedDatasets = computed(() => {
    return datasets.value.map(dataset => ({
      dataset_id: dataset.id,
      dataset_title: dataset.name || '',
      dataset_description: dataset.description || '',
      dataset_file_name: dataset.file_name || '',
      dataset_file_size: dataset.file_size || '',
      dataset_file_extension: dataset.file_extension || '',
      dataset_file_schema: dataset.schema || [],
      seller_id: dataset.owner_user_id,
      file: createDatasetFileObject(dataset),
      status: dataset.status || '',
      delivery_method: dataset.delivery_method || '',
      price: dataset.price || '',
      currency: dataset.currency || '',
      image: dataset.image || '',
      eval_id: dataset.eval_id || '',
      external_source_id: dataset.external_source_id || ''
    }));
  });

  const fetchDatasetsPage = async (page) => {
    isLoading.value = true;
    try {
  
      if (page === 1) {
        currentPage.value = 1;
        datasets.value = [];
        hasMoreItems.value = true;
      }
      
      const skip = (page - 1) * pageSize.value;
      const datasetsData = await fetchDatasets(skip, pageSize.value, 'not-eval');
     
      if (page === 1) {
        datasets.value = datasetsData;
      } else {
        datasets.value = [...datasets.value, ...datasetsData];
      }
      
      hasMoreItems.value = datasetsData.length >= pageSize.value;
          
    } catch (error) {
      console.error('Error fetching datasets:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveDataset = async (datasetData, editingItemId = null) => {
    try {
      if (editingItemId) {
        await updateDataset(editingItemId, datasetData);
        const index = datasets.value.findIndex(d => d.id === editingItemId);
        if (index !== -1) {
          datasets.value[index] = {
            ...datasets.value[index],
            name: datasetData.dataset.name,
            description: datasetData.dataset.description,
            file_name: datasetData.dataset.file_name,
            file_size: datasetData.dataset.file_size,
            file_extension: datasetData.dataset.file_extension,
            schema: datasetData.dataset.schema,
            seller_id: datasetData.dataset.owner_user_id
          };
        }
        addToast('Dataset updated successfully!', 'success');
        return true;
      } else {
        
        const response = await createDataset(datasetData);
        const newDataset = {
          id: response.id,
          name: response.name || datasetData.dataset.name,
          description: response.description || datasetData.dataset.description,
          file_name: response.file_name || datasetData.dataset.file_name,
          file_size: response.file_size || datasetData.dataset.file_size,
          file_extension: response.file_extension,
          schema: response.schema || [],
          seller_id: response.owner_user_id || datasetData.dataset.owner_user_id
        };
        datasets.value.unshift(newDataset);
        addToast('New dataset created successfully!', 'success');
        return true;
      }
    } catch (error) {
      console.error('Error saving dataset:', error);
      addToast('Error saving dataset. Please try again.', 'error');
      return false;
    }
  };

  const createDatasetFileObject = (selectedDataset) => {
    if (!selectedDataset) return null;
    const label = selectedDataset.eval_id ? 'Only Eval' : 'External';
    const fileName = selectedDataset.file_name || label;

    let fileSize = '0 KB';
    if (selectedDataset.file_size) {
      if (typeof selectedDataset.file_size === 'string') {
        fileSize = selectedDataset.file_size;
      } else {
        fileSize = `${(selectedDataset.file_size / 1024).toFixed(2)} KB`;
      }
    }

    const schema = Array.isArray(selectedDataset.schema)
      ? selectedDataset.schema
      : selectedDataset.schema?.fields || [];

    const normalizedSchema = schema.map(field => ({
      name: field.name || field.column_name || '',
      type: field.type || field.data_type || 'string',
      description: field.description || ''
    }));

    return {
      id: selectedDataset.id.toString(),
      name: fileName,
      path: selectedDataset.file_path || `/datasets/${selectedDataset.id}`,
      size: fileSize,
      type: 'parquet',
      schema: normalizedSchema,
      dataset_id: selectedDataset.id,
      created_at: selectedDataset.created_at,
      updated_at: selectedDataset.updated_at,
      rows: selectedDataset.rows || 0
    };
  };

  const loadMore = async () => {
    if (!hasMoreItems.value || isLoading.value) return;
    currentPage.value += 1;
    await fetchDatasetsPage(currentPage.value);
  };

  return {
    datasets,
    processedDatasets,
    isLoading,
    hasMoreItems,
    fetchDatasetsPage,
    saveDataset,
    createDatasetFileObject,
    loadMore
  };
}